import { useEffect } from "react";
import { Link } from "react-router-dom";
import { Mail } from "lucide-react";
import Navbar from "@/components/landing/Navbar";
import Footer from "@/components/landing/Footer";
import { Star } from "@/components/landing/Logo";
import { Reveal } from "@/components/landing/Reveal";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import GuideDownload from "@/components/GuideDownload";
import seoDe from "@/data/seo/de";
import seoEn from "@/data/seo/en";
import { useLang } from "@/i18n/LanguageContext";

export const SEO_SLUGS = ["smart-mirror", "smart-mirror-kosten", "smart-mirror-boutique", "smart-mirror-anbieter", "virtuelle-anprobe-spiegel", "virtual-try-on", "glossar", "style-coach-app"];

const renderInline = (text) =>
  text.split(/(\*\*[^*]+\*\*|\[[^\]]+\]\([^)]+\))/g).map((p, i) => {
    if (p.startsWith("**") && p.endsWith("**")) return <strong key={i} className="font-semibold">{p.slice(2, -2)}</strong>;
    const m = p.match(/^\[([^\]]+)\]\(([^)]+)\)$/);
    if (m) return <Link key={i} to={m[2]} className="gold-underline font-medium transition-colors duration-300 hover:text-[var(--camel)]" style={{ color: "var(--camel)" }}>{m[1]}</Link>;
    return p;
  });

const Block = ({ b }) => {
  if (b.t === "p") return <p className="mt-4 text-base leading-relaxed" style={{ color: "var(--muted-warm)" }}>{renderInline(b.x)}</p>;
  if (b.t === "ul" || b.t === "ol") {
    const Tag = b.t;
    return (
      <Tag className={`mt-4 space-y-3 ${b.t === "ol" ? "list-decimal" : "list-disc"} pl-5 marker:font-semibold marker:text-[var(--camel)]`}>
        {b.items.map((it, i) => (
          <li key={i} className="text-base leading-relaxed" style={{ color: "var(--muted-warm)" }}>{renderInline(it)}</li>
        ))}
      </Tag>
    );
  }
  if (b.t === "table")
    return (
      <div className="mt-5 overflow-x-auto rounded-2xl border" style={{ borderColor: "var(--line)" }}>
        <table className="w-full text-left text-sm">
          <thead>
            <tr style={{ background: "var(--surface-secondary)" }}>
              {b.head.map((h, i) => (
                <th key={i} className="px-4 py-3 font-semibold">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {b.rows.map((r, i) => (
              <tr key={i} className="border-t" style={{ borderColor: "var(--line)" }}>
                {r.map((c, j) => (
                  <td key={j} className="px-4 py-3 align-top" style={{ color: "var(--muted-warm)" }}>{renderInline(c)}</td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  return null;
};

export default function SeoPage({ slug }) {
  const { lang } = useLang();
  const data = (lang === "de" ? seoDe[slug] : seoEn[slug]) || seoEn[slug] || seoDe[slug];

  useEffect(() => {
    window.scrollTo(0, 0);
    const timer = setTimeout(() => {
      document.title = data.metaTitle;
      const desc = document.querySelector('meta[name="description"]');
      if (desc) desc.setAttribute("content", data.metaDescription);
    }, 0);
    const ld = document.createElement("script");
    ld.type = "application/ld+json";
    ld.id = "seo-page-ld";
    ld.textContent = JSON.stringify({
      "@context": "https://schema.org",
      "@graph": [
        {
          "@type": "FAQPage",
          inLanguage: lang === "de" ? "de" : "en",
          mainEntity: data.faq.map((x) => ({ "@type": "Question", name: x.q, acceptedAnswer: { "@type": "Answer", text: x.a } })),
        },
        {
          "@type": "BreadcrumbList",
          itemListElement: [
            { "@type": "ListItem", position: 1, name: "Mira Mirror", item: "https://mira-mirror.ai/" },
            { "@type": "ListItem", position: 2, name: "Smart Mirror", item: "https://mira-mirror.ai/smart-mirror" },
            { "@type": "ListItem", position: 3, name: data.h1, item: `https://mira-mirror.ai/${slug}` },
          ],
        },
      ],
    });
    document.head.appendChild(ld);
    return () => {
      clearTimeout(timer);
      document.getElementById("seo-page-ld")?.remove();
    };
  }, [data, lang, slug]);

  return (
    <main style={{ background: "var(--surface)", color: "var(--on-surface)" }} data-testid={`seo-page-${slug}`}>
      <Navbar />

      <article className="mx-auto max-w-3xl px-5 sm:px-8 pt-32 pb-12 sm:pt-40">
        <Reveal>
          <nav className="text-xs" style={{ color: "var(--muted-warm)" }} data-testid="seo-breadcrumb">
            <Link to="/" className="hover:text-[var(--camel)]">Mira Mirror</Link>
            <span className="mx-2">/</span>
            {slug === "smart-mirror" ? <span>Smart Mirror</span> : (
              <>
                <Link to="/smart-mirror" className="hover:text-[var(--camel)]">Smart Mirror</Link>
                <span className="mx-2">/</span>
                <span>{data.kicker}</span>
              </>
            )}
          </nav>
          <p className="mt-6 flex items-center gap-2 text-xs uppercase font-medium" style={{ color: "var(--camel)", letterSpacing: "0.2em" }}>
            <Star size={12} /> {data.kicker}
          </p>
          <h1 className="mt-4 font-serif-display text-4xl sm:text-5xl font-semibold leading-[1.1]" data-testid="seo-title">{data.h1}</h1>
          <p className="mt-6 text-lg leading-relaxed" style={{ color: "var(--muted-warm)" }}>{data.intro}</p>

          {data.keyFacts && (
            <div className="mt-8 rounded-3xl border p-6" style={{ borderColor: "var(--gold)", background: "var(--surface-secondary)" }} data-testid="seo-keyfacts">
              <p className="text-xs font-bold uppercase" style={{ letterSpacing: "0.14em", color: "var(--camel)" }}>{data.keyFactsTitle}</p>
              <ul className="mt-3 space-y-2.5">
                {data.keyFacts.map((f, i) => (
                  <li key={i} className="flex items-start gap-2.5 text-sm leading-relaxed">
                    <Star size={11} className="mt-1.5 shrink-0" style={{ color: "var(--gold)" }} />
                    <span>{renderInline(f)}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {data.image && (
            <img src={data.image.src} alt={data.image.alt} loading="lazy" className="mt-8 w-full rounded-3xl border object-cover" style={{ borderColor: "var(--line)", maxHeight: "440px" }} data-testid="seo-image" />
          )}

          {slug === "smart-mirror" && <GuideDownload />}
        </Reveal>

        {data.sections.map((s, i) => (
          <Reveal key={i} delay={0.05}>
            <section className="mt-12">
              <h2 className="font-serif-display text-2xl sm:text-3xl font-semibold leading-snug">{s.h2}</h2>
              {s.blocks.map((b, j) => <Block key={j} b={b} />)}
            </section>
          </Reveal>
        ))}

        <section className="mt-14 border-t pt-10" style={{ borderColor: "var(--line)" }} data-testid="seo-faq">
          <h2 className="font-serif-display text-2xl sm:text-3xl font-semibold">{data.faqTitle}</h2>
          <Accordion type="single" collapsible className="mt-4">
            {data.faq.map((x, i) => (
              <AccordionItem key={i} value={`faq-${i}`} style={{ borderColor: "var(--line)" }}>
                <AccordionTrigger className="text-left font-medium hover:no-underline" data-testid={`seo-faq-q-${i}`}>{x.q}</AccordionTrigger>
                <AccordionContent className="text-sm leading-relaxed" style={{ color: "var(--muted-warm)" }}>{x.a}</AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </section>
      </article>

      <section className="border-t text-center" style={{ borderColor: "var(--line)", background: "var(--surface-secondary)" }}>
        <div className="mx-auto max-w-2xl px-5 py-16" data-testid="seo-cta">
          <h2 className="font-serif-display text-3xl font-semibold">{data.cta.title}</h2>
          <p className="mx-auto mt-3 max-w-md text-base" style={{ color: "var(--muted-warm)" }}>{data.cta.sub}</p>
          <div className="mt-7 flex flex-wrap items-center justify-center gap-4">
            <Link to="/kontakt" data-testid="seo-cta-contact" className="rounded-full px-7 py-3.5 text-sm font-semibold text-white transition-transform duration-300 hover:-translate-y-0.5" style={{ background: "var(--camel)" }}>
              {data.cta.btn}
            </Link>
            <a href={`mailto:${data.cta.mail}`} data-testid="seo-cta-mail" className="flex items-center gap-2 text-sm font-medium transition-colors duration-300 hover:text-[var(--camel)]">
              <Mail size={15} /> {data.cta.mail}
            </a>
          </div>
        </div>
      </section>

      <Footer />
    </main>
  );
}
